import { useState, useEffect, useMemo } from "react";
import {
  Icons, BOOKING_TYPES, SALE_TYPES, ADJUSTMENT_TYPES, ADJUSTMENT_MODES,
  COUNTRIES as BOOKING_COUNTRIES, generateAdjustmentId, generateNoteId,
} from "./bookingConstants";
import { usePrivateCustomersList, useProfessionalCustomersList } from "../../api/people/usePeopleApi";
import styles from "./StepBookingDetails.module.css";

const NATIONALITIES = [
  "American", "Australian", "Austrian", "Belgian", "Brazilian", "British",
  "Canadian", "Chinese", "Czech", "Danish", "Dutch", "Finnish", "French",
  "German", "Greek", "Hungarian", "Indian", "Irish", "Italian", "Japanese",
  "Korean", "Mexican", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "Spanish", "Swedish", "Swiss", "Turkish", "Ukrainian",
].sort();

const LANGUAGES = [
  "Arabic", "Chinese", "Danish", "Dutch", "English", "Finnish",
  "French", "German", "Greek", "Hungarian", "Italian", "Japanese",
  "Korean", "Norwegian", "Polish", "Portuguese", "Romanian",
  "Russian", "Spanish", "Swedish", "Turkish", "Ukrainian",
].sort();

const GENDERS = ["Male", "Female", "Other", "Prefer not to say"];

const COUNTRIES = [
  "Austria", "Belgium", "Brazil", "Canada", "China", "Czech Republic",
  "Denmark", "Finland", "France", "Germany", "Greece", "Hungary",
  "India", "Ireland", "Italy", "Japan", "Mexico", "Netherlands",
  "Norway", "Poland", "Portugal", "Romania", "Russia", "South Korea",
  "Spain", "Sweden", "Switzerland", "Turkey", "Ukraine",
  "United Kingdom", "United States",
].sort();

const INDUSTRIES = [
  "Airline", "Corporate Travel", "Event Management", "Government",
  "Hospitality", "Incentive Travel", "Logistics", "Management Consulting",
  "Media & Entertainment", "NGO / Non-Profit", "Sports & Recreation",
  "Technology", "Tour Operator", "Travel Agency", "Other",
].sort();

const CONTACT_ROLES = [
  "Accounts Manager", "CEO / Director", "Finance Manager",
  "HR Manager", "Operations Manager", "PA / Assistant",
  "Procurement Manager", "Travel Coordinator", "Other",
];

const PAGE_SIZE = 6;

const mapPrivateToUi = (c) => ({
  id: c.id,
  type: "Private",
  name: `${c.firstName ?? ""} ${(c.lastName ?? "").toUpperCase()}`.trim() || c.customerCode || "—",
  email: c.email ?? "",
  phone: c.phone ?? "",
  country: c.country ?? "",
  customerCode: c.customerCode,
  streetName: c.street ?? "",
  streetNo: "",
  city: c.city ?? "",
  postalCode: c.postalCode ?? "",
});

const mapCompanyToUi = (c) => ({
  id: c.id,
  type: "Company",
  name: c.tradingName || c.legalName || c.customerCode || "—",
  email: c.invoiceEmail ?? c.primaryContactEmail ?? "",
  phone: c.primaryContactPhone ?? "",
  country: c.country ?? "",
  customerCode: c.customerCode,
  streetName: c.street ?? "",
  streetNo: "",
  city: c.city ?? "",
  postalCode: c.postalCode ?? "",
});

const formatAddress = (c) => {
  if (!c) return null;
  const parts = [
    c.streetName && `${c.streetName}${c.streetNo ? ` ${c.streetNo}` : ""}`,
    c.houseNo && `House ${c.houseNo}`,
    c.boxNo && `Box ${c.boxNo}`,
    c.city,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
};

const StepBookingDetails = ({ data, onChange }) => {
  const [custQuery, setCustQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showNewCust, setShowNewCust] = useState(false);
  const [custTab, setCustTab] = useState(data.customerType || "Private");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(custQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [custQuery]);

  useEffect(() => { setPage(1); }, [debouncedQuery, custTab]);

  const isPrivate = custTab === "Private";

  const privateParams = { q: isPrivate ? debouncedQuery : "", status: "ACTIVE", page: isPrivate ? page : 1, limit: PAGE_SIZE };
  const companyParams = { q: !isPrivate ? debouncedQuery : "", status: "ACTIVE", page: !isPrivate ? page : 1, limit: PAGE_SIZE };

  const privateRes = usePrivateCustomersList(privateParams);
  const companyRes = useProfessionalCustomersList(companyParams);

  const active = isPrivate ? privateRes : companyRes;
  const customers = useMemo(() => {
    const rows = active.data || [];
    return isPrivate ? rows.map(mapPrivateToUi) : rows.map(mapCompanyToUi);
  }, [active.data, isPrivate]);

  const totalPages = active.pagination?.totalPages || 1;
  const total = active.pagination?.total ?? customers.length;

  const handleTabSwitch = (tab) => {
    setCustTab(tab);
    setCustQuery("");
    if (!data.customer) onChange("customerType", tab);
  };

  const selectCustomer = (c) => { onChange("customer", c); onChange("customerType", c.type); onChange("isNewCustomer", false); setShowNewCust(false); setCustQuery(""); };
  const handleNewField = (field, value) => onChange("newCustomer", { ...data.newCustomer, [field]: value });

  return (
    <div className={styles.stepContent}>
      {/* Booking Type — hidden while registering a new customer (always Offline). */}
      {!data.isNewCustomer && (
        <div className={styles.sectionBlock}>
          <div className={styles.sectionLabel}>Booking Type</div>
          <div className={styles.typeRow}>
            {BOOKING_TYPES.map((bt) => (
              <button key={bt.key} className={`${styles.typeOption} ${data.bookingType === bt.key ? styles.typeOptionActive : ""}`} onClick={() => onChange("bookingType", bt.key)}>
                <div className={styles.typeOptionRadio}>{data.bookingType === bt.key && <div className={styles.typeOptionDot} />}</div>
                <div><div className={styles.typeOptionLabel}>{bt.label}</div><div className={styles.typeOptionDesc}>{bt.desc}</div></div>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* <div className={styles.divider} /> */}
      {/* <div className={styles.sectionBlock}>
        <div className={styles.sectionLabel}>Sale Type</div>
        <div className={styles.typeRow}>
          {SALE_TYPES.map((st) => (
            <button key={st.key} className={`${styles.typeOption} ${data.saleType === st.key ? styles.typeOptionActive : ""}`} onClick={() => onChange("saleType", st.key)}>
              <div className={styles.typeOptionRadio}>{data.saleType === st.key && <div className={styles.typeOptionDot} />}</div>
              <div><div className={styles.typeOptionLabel}>{st.label}</div><div className={styles.typeOptionDesc}>{st.desc}</div></div>
            </button>
          ))}
        </div>
      </div> */}
      {/* <div className={styles.divider} /> */}

      {/* <div className={styles.sectionBlock}>
        <div className={styles.sectionLabelRow}>
          <div className={styles.sectionLabel}>Pricing Adjustments</div>
          <button className={styles.addBtn} onClick={() => onChange("adjustments", [...(data.adjustments || []), { id: generateAdjustmentId(), type: "Discount", mode: "Fixed", value: 0, description: "" }])}>{Icons.plus} Add Adjustment</button>
        </div>
        {(data.adjustments || []).length > 0 ? (
          <div className={styles.adjustmentList}>
            {data.adjustments.map((adj, idx) => (
              <div key={adj.id} className={styles.adjustmentRow}>
                <select className={styles.input} value={adj.type} onChange={(e) => {
                  const next = [...data.adjustments]; next[idx] = { ...next[idx], type: e.target.value }; onChange("adjustments", next);
                }}>
                  {ADJUSTMENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
                <select className={styles.input} value={adj.mode} onChange={(e) => {
                  const next = [...data.adjustments]; next[idx] = { ...next[idx], mode: e.target.value }; onChange("adjustments", next);
                }}>
                  {ADJUSTMENT_MODES.map(m => <option key={m}>{m}</option>)}
                </select>
                <input className={styles.input} type="number" step="0.01" placeholder={adj.mode === "Percentage" ? "%" : "€"} value={adj.value || ""} onChange={(e) => {
                  const next = [...data.adjustments]; next[idx] = { ...next[idx], value: parseFloat(e.target.value) || 0 }; onChange("adjustments", next);
                }} onFocus={(e) => e.target.select()} style={{ width: 100 }} />
                <input className={styles.input} placeholder="Description" value={adj.description} onChange={(e) => {
                  const next = [...data.adjustments]; next[idx] = { ...next[idx], description: e.target.value }; onChange("adjustments", next);
                }} style={{ flex: 1 }} />
                <button className={styles.pcRemove} onClick={() => onChange("adjustments", data.adjustments.filter((_, i) => i !== idx))}>{Icons.trash}</button>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.infoBox}>No pricing adjustments. Add discounts or surcharges at booking level.</div>
        )}
      </div> */}
      <div className={styles.divider} />

      <div className={styles.sectionBlock}>
        <div className={styles.sectionLabelRow}>
          <div className={styles.sectionLabel}>Customer</div>
        </div>
        {data._prefillCustomer && !showNewCust ? (
          <div className={styles.selectedCustomer}>
            <div className={styles.scAvatar} style={{ background: data._prefillCustomer.type === "Company" ? "var(--accent-blue)" : "var(--accent-amber)" }}>{data._prefillCustomer.name[0]}</div>
            <div className={styles.scInfo}>
              <div className={styles.scName}>{data._prefillCustomer.name}</div>
              <div className={styles.scMeta}>{data._prefillCustomer.email} · {data._prefillCustomer.phone} · {data._prefillCustomer.country}</div>
              {formatAddress(data._prefillCustomer) && (
                <div className={styles.scAddress}>{Icons.mapPin} {formatAddress(data._prefillCustomer)}</div>
              )}
            </div>
            <span className={`${styles.scTypeBadge} ${data._prefillCustomer.type === "Company" ? styles.scTypeBadgeCompany : ""}`}>{data._prefillCustomer.type}</span>
            <button className={styles.scChangeBtn} onClick={() => { onChange("_prefillCustomer", null); onChange("customer", null); }}>Change</button>
          </div>
        ) : data.customer && !showNewCust ? (
          <div className={styles.selectedCustomer}>
            <div className={styles.scAvatar}>{data.customer.name[0]}</div>
            <div className={styles.scInfo}>
              <div className={styles.scName}>{data.customer.name}</div>
              <div className={styles.scMeta}>{data.customer.email} · {data.customer.phone} · {data.customer.country} · {data.customer.totalBookings} bookings</div>
              {formatAddress(data.customer) && (
                <div className={styles.scAddress}>{Icons.mapPin} {formatAddress(data.customer)}</div>
              )}
            </div>
            <span className={`${styles.scTypeBadge} ${data.customer.type === "Company" ? styles.scTypeBadgeCompany : ""}`}>{data.customer.type}</span>
            <button className={styles.scChangeBtn} onClick={() => onChange("customer", null)}>Change</button>
          </div>
        ) : !showNewCust ? (
          <>
            <div className={styles.custTabs}>
              {["Private", "Company"].map((tab) => {
                const tabTotal = tab === "Private" ? (privateRes.pagination?.total ?? 0) : (companyRes.pagination?.total ?? 0);
                return (
                  <button key={tab} className={`${styles.custTab} ${custTab === tab ? styles.custTabActive : ""}`} onClick={() => handleTabSwitch(tab)}>
                    {tab === "Private" ? Icons.user : Icons.building}
                    <span>{tab}</span>
                    <span className={styles.custTabCount}>{tabTotal}</span>
                  </button>
                );
              })}
            </div>
            <div className={styles.searchWrap}><span className={styles.searchSvg}>{Icons.search}</span><input className={styles.searchField} placeholder={`Search ${custTab.toLowerCase()} customers by name or email...`} value={custQuery} onChange={(e) => setCustQuery(e.target.value)} />{custQuery && <button className={styles.searchClearBtn} onClick={() => setCustQuery("")}>{Icons.x}</button>}</div>
            <div className={styles.customerGrid}>
              {active.loading && customers.length === 0 ? (
                <div className={styles.custEmptyTab}>Loading {custTab.toLowerCase()} customers…</div>
              ) : active.error ? (
                <div className={styles.custEmptyTab}>{active.error}</div>
              ) : customers.length === 0 ? (
                <div className={styles.custEmptyTab}>No {custTab.toLowerCase()} customers found{debouncedQuery ? ` for "${debouncedQuery}"` : ""}</div>
              ) : customers.map((c) => (
                <button key={c.id} className={styles.custCard} onClick={() => selectCustomer(c)}>
                  <div className={styles.custCardAvatar} style={{ background: c.type === "Company" ? "var(--blue-50)" : "var(--amber-50)", color: c.type === "Company" ? "var(--accent-blue)" : "var(--accent-amber)" }}>{c.name[0]}</div>
                  <div className={styles.custCardInfo}><div className={styles.custCardName}>{c.name}</div><div className={styles.custCardEmail}>{c.email}</div></div>
                  <span className={`${styles.custCardType} ${c.type === "Company" ? styles.custCardTypeCompany : ""}`}>{c.type}</span>
                </button>
              ))}
            </div>
            {totalPages > 1 && (
              <div className={styles.custPagination}>
                <button className={styles.custPageBtn} disabled={page <= 1 || active.loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>{Icons.arrowLeft} Prev</button>
                <span className={styles.custPageInfo}>Page {page} of {totalPages}{total ? ` · ${total} total` : ""}</span>
                <button className={styles.custPageBtn} disabled={page >= totalPages || active.loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next {Icons.arrowRight}</button>
              </div>
            )}
            <button className={styles.newCustToggle} onClick={() => { setShowNewCust(true); onChange("customerType", custTab); onChange("isNewCustomer", true); onChange("bookingType", "Offline"); }}>{Icons.plus} Register new {custTab.toLowerCase()} customer</button>
          </>
        ) : (
          <div className={styles.newCustForm}>
            <div className={styles.newCustHeader}>
              <button
                type="button"
                className={styles.newCustBack}
                onClick={() => { setShowNewCust(false); onChange("isNewCustomer", false); }}
                title="Back to existing customers"
                aria-label="Back to existing customers"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </button>
              <span className={styles.newCustTitle}>New {data.customerType} Customer</span>
            </div>

            {data.customerType === "Company" ? (
              <>
                {/* ── Company Identity ── */}
                <div className={styles.newCustSectionTitle}>Company Identity</div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Trading Name <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. Horizon Travel Group" value={data.newCustomer.companyName} onChange={(e) => handleNewField("companyName", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Legal Name <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. Horizon Travel Group NV" value={data.newCustomer.legalName} onChange={(e) => handleNewField("legalName", e.target.value)} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>VAT Number <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. BE0477.123.456" value={data.newCustomer.vatNumber} onChange={(e) => handleNewField("vatNumber", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Industry <span className={styles.req}>*</span></label><select className={styles.input} value={data.newCustomer.industry} onChange={(e) => handleNewField("industry", e.target.value)}><option value="">Select industry...</option>{INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}</select></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Website</label><input className={styles.input} placeholder="e.g. https://www.company.com" value={data.newCustomer.website} onChange={(e) => handleNewField("website", e.target.value)} /></div>
                </div>

                {/* ── Registered Address ── */}
                <div className={styles.newCustSectionTitle}>Registered Address</div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Street</label><input className={styles.input} placeholder="e.g. Rue de la Loi 42" value={data.newCustomer.street} onChange={(e) => handleNewField("street", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>City</label><input className={styles.input} placeholder="e.g. Brussels" value={data.newCustomer.city} onChange={(e) => handleNewField("city", e.target.value)} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Zip / Postal Code</label><input className={styles.input} placeholder="e.g. 1000" value={data.newCustomer.zip} onChange={(e) => handleNewField("zip", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Country <span className={styles.req}>*</span></label><select className={styles.input} value={data.newCustomer.country} onChange={(e) => handleNewField("country", e.target.value)}><option value="">Select country...</option>{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                </div>

                {/* ── Invoicing Details ── */}
                <div className={styles.newCustSectionTitle}>Invoicing Details</div>
                <div className={styles.formRow}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Company Has Invoice Email</label>
                    <div className={styles.yesNoToggle}>
                      <button type="button" className={`${styles.yesNoBtn} ${data.newCustomer.hasInvoiceEmail !== false ? styles.yesNoBtnYes : ""}`} onClick={() => handleNewField("hasInvoiceEmail", true)}>YES</button>
                      <button type="button" className={`${styles.yesNoBtn} ${data.newCustomer.hasInvoiceEmail === false ? styles.yesNoBtnNo : ""}`} onClick={() => { handleNewField("hasInvoiceEmail", false); handleNewField("invoiceEmail", ""); }}>NO</button>
                    </div>
                  </div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Payment Terms</label><select className={styles.input} value={data.newCustomer.paymentTerms} onChange={(e) => handleNewField("paymentTerms", e.target.value)}><option value="0">Immediate (0 days)</option><option value="7">7 days</option><option value="14">14 days</option><option value="30">30 days</option><option value="45">45 days</option><option value="60">60 days</option></select></div>
                </div>
                {data.newCustomer.hasInvoiceEmail !== false && (
                  <div className={styles.formRow}>
                    <div className={styles.field}><label className={styles.fieldLabel}>Invoice Email <span className={styles.req}>*</span></label><input className={styles.input} type="email" placeholder="e.g. accounts@company.com" value={data.newCustomer.invoiceEmail} onChange={(e) => handleNewField("invoiceEmail", e.target.value.trim().toLowerCase())} style={{ textTransform: "lowercase" }} /></div>
                  </div>
                )}
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Invoicing Address</label><input className={styles.input} placeholder="Full invoicing address (if different)" value={data.newCustomer.invoiceAddress} onChange={(e) => handleNewField("invoiceAddress", e.target.value)} /></div>
                </div>

                {/* ── Primary Contact ── */}
                <div className={styles.newCustSectionTitle}>Primary Contact</div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>First Name <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. Thomas" value={data.newCustomer.contactFirstName} onChange={(e) => handleNewField("contactFirstName", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Last Name <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. LECLERCQ" value={data.newCustomer.contactLastName} onChange={(e) => handleNewField("contactLastName", e.target.value.toUpperCase())} style={{ textTransform: "uppercase" }} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Contact Has Email</label>
                    <div className={styles.yesNoToggle}>
                      <button type="button" className={`${styles.yesNoBtn} ${data.newCustomer.hasContactEmail !== false ? styles.yesNoBtnYes : ""}`} onClick={() => handleNewField("hasContactEmail", true)}>YES</button>
                      <button type="button" className={`${styles.yesNoBtn} ${data.newCustomer.hasContactEmail === false ? styles.yesNoBtnNo : ""}`} onClick={() => onChange("newCustomer", { ...data.newCustomer, hasContactEmail: false, contactEmail: "" })}>NO</button>
                    </div>
                  </div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Phone <span className={styles.req}>*</span></label><input className={styles.input} type="tel" placeholder="e.g. +32 2 234 567 8" value={data.newCustomer.contactPhone} onChange={(e) => handleNewField("contactPhone", e.target.value.replace(/[^\d+\s\-\.\(\)]/g, ''))} /><span className={styles.phoneDisplay}>E.164 format with country code</span></div>
                </div>
                {data.newCustomer.hasContactEmail !== false && (
                  <div className={styles.formRow}>
                    <div className={styles.field}><label className={styles.fieldLabel}>Email <span className={styles.req}>*</span></label><input className={styles.input} type="email" placeholder="e.g. t.leclercq@company.com" value={data.newCustomer.contactEmail} onChange={(e) => handleNewField("contactEmail", e.target.value.trim().toLowerCase())} style={{ textTransform: "lowercase" }} /></div>
                  </div>
                )}
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Role / Title</label><select className={styles.input} value={data.newCustomer.contactRole} onChange={(e) => handleNewField("contactRole", e.target.value)}><option value="">Select role...</option>{CONTACT_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Preferred Language</label><select className={styles.input} value={data.newCustomer.contactLanguage} onChange={(e) => handleNewField("contactLanguage", e.target.value)}><option value="">Select language...</option>{LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
                </div>
              </>
            ) : (
              <>
                {/* ── Personal Information ── */}
                <div className={styles.newCustSectionTitle}>Personal Information</div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>First Name <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. Sarah" value={data.newCustomer.firstName} onChange={(e) => handleNewField("firstName", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Last Name <span className={styles.req}>*</span></label><input className={styles.input} placeholder="e.g. JOHNSON" value={data.newCustomer.lastName} onChange={(e) => handleNewField("lastName", e.target.value.toUpperCase())} style={{ textTransform: "uppercase" }} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Date of Birth</label><input className={styles.input} type="date" value={data.newCustomer.dateOfBirth} onChange={(e) => handleNewField("dateOfBirth", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Gender</label><select className={styles.input} value={data.newCustomer.gender} onChange={(e) => handleNewField("gender", e.target.value)}><option value="">Select gender...</option>{GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}</select></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Nationality <span className={styles.req}>*</span></label><select className={styles.input} value={data.newCustomer.nationality} onChange={(e) => handleNewField("nationality", e.target.value)}><option value="">Select nationality...</option>{NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Preferred Language <span className={styles.req}>*</span></label><select className={styles.input} value={data.newCustomer.language} onChange={(e) => handleNewField("language", e.target.value)}><option value="">Select language...</option>{LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}</select></div>
                </div>

                {/* ── Contact Information ── */}
                <div className={styles.newCustSectionTitle}>Contact Information</div>
                <div className={styles.formRow}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel}>Client Has Email Address</label>
                    <div className={styles.yesNoToggle}>
                      <button type="button" className={`${styles.yesNoBtn} ${data.newCustomer.hasEmail !== false ? styles.yesNoBtnYes : ""}`} onClick={() => handleNewField("hasEmail", true)}>YES</button>
                      <button type="button" className={`${styles.yesNoBtn} ${data.newCustomer.hasEmail === false ? styles.yesNoBtnNo : ""}`} onClick={() => onChange("newCustomer", { ...data.newCustomer, hasEmail: false, email: "" })}>NO</button>
                    </div>
                  </div>
                  <div className={styles.field} />
                </div>
                {data.newCustomer.hasEmail !== false && (
                  <div className={styles.formRow}>
                    <div className={styles.field}><label className={styles.fieldLabel}>Email <span className={styles.req}>*</span></label><input className={styles.input} type="email" placeholder="e.g. sarah.j@email.com" value={data.newCustomer.email} onChange={(e) => handleNewField("email", e.target.value.trim().toLowerCase())} style={{ textTransform: "lowercase" }} /></div>
                  </div>
                )}
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Phone <span className={styles.req}>*</span></label><input className={styles.input} type="tel" placeholder="e.g. +32 475 123 456" value={data.newCustomer.phone} onChange={(e) => handleNewField("phone", e.target.value.replace(/[^\d+\s\-\.\(\)]/g, ''))} /><span className={styles.phoneDisplay}>E.164 format with country code (e.g. +32475123456)</span></div>
                </div>

                {/* ── Address ── */}
                <div className={styles.newCustSectionTitle}>Address</div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Street</label><input className={styles.input} placeholder="e.g. Rue de la Loi" value={data.newCustomer.street} onChange={(e) => handleNewField("street", e.target.value)} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>House No.</label><input className={styles.input} placeholder="e.g. 42" value={data.newCustomer.houseNumber} onChange={(e) => handleNewField("houseNumber", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>Box No.</label><input className={styles.input} placeholder="e.g. 3A" value={data.newCustomer.boxNumber} onChange={(e) => handleNewField("boxNumber", e.target.value)} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Zip / Postal Code</label><input className={styles.input} placeholder="e.g. 1000" value={data.newCustomer.zip} onChange={(e) => handleNewField("zip", e.target.value)} /></div>
                  <div className={styles.field}><label className={styles.fieldLabel}>City</label><input className={styles.input} placeholder="e.g. Brussels" value={data.newCustomer.city} onChange={(e) => handleNewField("city", e.target.value)} /></div>
                </div>
                <div className={styles.formRow}>
                  <div className={styles.field}><label className={styles.fieldLabel}>Country</label><select className={styles.input} value={data.newCustomer.country} onChange={(e) => handleNewField("country", e.target.value)}><option value="">Select country...</option>{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.divider} />
      {/* <div className={styles.sectionBlock}>
        <div className={styles.sectionLabelRow}>
          <div className={styles.sectionLabel}>Notes & Logs</div>
          <button className={styles.addBtn} onClick={() => onChange("notes", [...(data.notes || []), { id: generateNoteId(), text: "", createdAt: new Date().toISOString(), createdBy: "Current User" }])}>{Icons.plus} Add Note</button>
        </div>
        {(data.notes || []).length > 0 ? (
          <div className={styles.notesList}>
            {data.notes.map((note, idx) => (
              <div key={note.id} className={styles.noteItem}>
                <div className={styles.noteMeta}>
                  <span className={styles.noteAuthor}>{note.createdBy}</span>
                  <span className={styles.noteDate}>{new Date(note.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}</span>
                  <button className={styles.pcRemove} onClick={() => onChange("notes", data.notes.filter((_, i) => i !== idx))} title="Remove note">{Icons.trash}</button>
                </div>
                <textarea className={`${styles.input} ${styles.textarea}`} placeholder="Type your note here..." rows={2} value={note.text} onChange={(e) => {
                  const next = [...data.notes]; next[idx] = { ...next[idx], text: e.target.value }; onChange("notes", next);
                }} />
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.infoBox}>No notes yet. Add internal notes, logs, or history entries.</div>
        )}
      </div> */}
    </div>
  );
};

export default StepBookingDetails;